/**
 * HP and armour bars under a combatant's name.
 *
 * Janeks: *"show HP and armor bars under names (only in battle mode, not in
 * home safezone settlement)."*
 *
 * ─── Why these are not baked into the name tag ──────────────────────────────
 * `name-tags.ts` caches its textures by `` `${faction}|${text}` `` precisely so
 * that sixteen "Settler" labels share one image. A bar drawn inside that SVG
 * would change on every hit, so every body would need its own texture, redrawn
 * and re-uploaded whenever anyone took damage — and the sharing that makes a
 * crowded settlement cheap would be gone.
 *
 * So the bars are separate quads stacked directly beneath the label, offset by
 * the same `NAME_CLEARANCE` arithmetic the tag itself uses. They carry no
 * texture at all: a bar is a rectangle, and scaling one is cheaper than
 * repainting an image.
 *
 * ─── Sprites, because the camera orbits ─────────────────────────────────────
 * They are `THREE.Sprite`s rather than `PlaneGeometry` meshes for the same
 * reason the name tags are: this camera rotates freely around the player, and a
 * flat plane laid in a fixed orientation is edge-on — literally invisible — from
 * a quarter of the possible angles. A sprite always faces the viewer.
 *
 * `sprite.center` then does the anchoring work: setting it to the LEFT edge
 * means scaling x shrinks the bar from the right, which is what a depleting bar
 * must do. A centred sprite would eat itself symmetrically and leave a
 * half-empty bar floating in the middle of its own track.
 *
 * ─── Battle mode ───────────────────────────────────────────────────────────
 * The "only in battle" half is not enforced here — this module draws what it is
 * handed. The gate is the caller: `arena-view.ts` builds it, and the settlement
 * (`index.ts`) does not. `MapCategory` in `data/maps/registry.ts` is the durable
 * form of that distinction, and it already says which maps are `battleground`.
 *
 * Armour deserves the second bar rather than being folded into HP: it is a
 * depletable shield that soaks 85% of every blow (health-recovery.md rule 3),
 * so a duel drawn with HP alone shows a player shrugging off most of the damage
 * for no visible reason.
 */
import * as THREE from 'three';

/** Matches `name-tags.ts` — the bars hang under the label, not through it. */
const NAME_CLEARANCE = 0.34;
const PLAYER_ANCHOR_HEIGHT = 1.62;

/** Gap between the label's baseline and the first bar. */
const BAR_DROP = 0.1;
const BAR_W = 0.9;
const BAR_H = 0.075;
const BAR_GAP = 0.02;

const COLOR_TRACK = 0x1b1613;
const COLOR_HP = 0xb8412f;
const COLOR_ARMOR = 0x7f97a8;

export interface CombatBarTarget {
  x: number;
  z: number;
  hp: number;
  maxHp: number;
  armor: number;
  /** 0 means "no armour pool at all" — the armour bar is then not drawn, rather than drawn empty. */
  maxArmor: number;
  /** Override the anchor height for a non-player body. */
  height?: number;
}

export interface CombatBarsHandles {
  object: THREE.Object3D;
  update(targets: readonly CombatBarTarget[]): void;
  dispose(): void;
}

/**
 * Fraction of a bar that should be filled, clamped and NaN-proofed.
 *
 * Pure and exported: the interesting cases are all degenerate ones (a zero
 * maximum, an over-heal, a negative current) and every one of them is a real
 * possibility on a wire that can arrive from an older server.
 */
export function barFill(current: number, max: number): number {
  if (!Number.isFinite(current) || !Number.isFinite(max) || max <= 0) return 0;
  return Math.min(1, Math.max(0, current / max));
}

interface Rig {
  group: THREE.Group;
  hpTrack: THREE.Sprite;
  hpFill: THREE.Sprite;
  armorTrack: THREE.Sprite;
  armorFill: THREE.Sprite;
}

function makeBar(material: THREE.SpriteMaterial): THREE.Sprite {
  const sprite = new THREE.Sprite(material);
  // Anchor at the left edge so a shrinking bar empties rightward — see the
  // header. `center` is in 0..1 sprite space, y 0.5 = vertically centred.
  sprite.center.set(0, 0.5);
  // Never a click target — same reason as the aim arc.
  sprite.raycast = () => {};
  return sprite;
}

export function createCombatBars(): CombatBarsHandles {
  const group = new THREE.Group();
  // Under the name tags (19) so a label always wins, but over the world.
  group.renderOrder = 18;

  const shared = {
    track: new THREE.SpriteMaterial({ color: COLOR_TRACK, transparent: true, opacity: 0.75, depthTest: false, depthWrite: false, fog: false }),
    hp: new THREE.SpriteMaterial({ color: COLOR_HP, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false, fog: false }),
    armor: new THREE.SpriteMaterial({ color: COLOR_ARMOR, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false, fog: false }),
  };

  // Grown on demand and never shrunk — a duel has two combatants, a PvE
  // instance up to eight, and recycling is what keeps `update` allocation-free
  // on the frames that matter.
  const rigs: Rig[] = [];

  const rigFor = (i: number): Rig => {
    if (rigs[i]) return rigs[i];
    const rigGroup = new THREE.Group();
    const hpTrack = makeBar(shared.track);
    const hpFill = makeBar(shared.hp);
    const armorTrack = makeBar(shared.track);
    const armorFill = makeBar(shared.armor);
    for (const m of [hpTrack, hpFill, armorTrack, armorFill]) {
      m.renderOrder = 18;
      rigGroup.add(m);
    }
    group.add(rigGroup);
    const rig: Rig = {
      group: rigGroup, hpTrack, hpFill, armorTrack, armorFill,
    };
    rigs[i] = rig;
    return rig;
  };

  /**
   * Sizes a fill bar. Anchoring is handled by `sprite.center` (see `makeBar`),
   * so the position is the track's own left edge in every case and only the
   * width changes.
   */
  const layFill = (sprite: THREE.Sprite, fraction: number, y: number): void => {
    const w = BAR_W * fraction;
    sprite.visible = w > 0.0001;
    sprite.scale.set(Math.max(w, 0.0001), BAR_H, 1);
    sprite.position.set(-BAR_W / 2, y, 0);
  };

  return {
    object: group,

    update(targets: readonly CombatBarTarget[]): void {
      for (let i = 0; i < targets.length; i += 1) {
        const t = targets[i];
        const rig = rigFor(i);
        rig.group.visible = true;

        const hasArmor = t.maxArmor > 0;
        const base = (t.height ?? PLAYER_ANCHOR_HEIGHT) + NAME_CLEARANCE - BAR_DROP;
        rig.group.position.set(t.x, base, t.z);

        const hpY = 0;
        const armorY = -(BAR_H + BAR_GAP);

        rig.hpTrack.scale.set(BAR_W, BAR_H, 1);
        rig.hpTrack.position.set(-BAR_W / 2, hpY, 0);
        layFill(rig.hpFill, barFill(t.hp, t.maxHp), hpY);

        rig.armorTrack.visible = hasArmor;
        rig.armorFill.visible = hasArmor;
        if (hasArmor) {
          rig.armorTrack.scale.set(BAR_W, BAR_H, 1);
          rig.armorTrack.position.set(-BAR_W / 2, armorY, 0);
          layFill(rig.armorFill, barFill(t.armor, t.maxArmor), armorY);
        }
      }

      // Hide the surplus rather than destroying it — the next frame very likely
      // wants it back.
      for (let i = targets.length; i < rigs.length; i += 1) rigs[i].group.visible = false;
    },

    dispose(): void {
      // Sprites share the three materials above and own no geometry of their
      // own, so disposing the materials once is the whole cleanup.
      shared.track.dispose();
      shared.hp.dispose();
      shared.armor.dispose();
      rigs.length = 0;
      group.clear();
    },
  };
}
